"use client";

import {
  useGetBusinessViaIdentifier,
  useGetSalonForBooking,
  useGetBundlesForBooking,
  useGetFaqs,
  useGetStorefrontProducts,
} from "@/lib/apollo/hooks/booking-edge";
import {
  useGetPublicSalonReviews,
  useGetPublicSalonPromos,
} from "@/lib/api/usePublicV2";
import { BookingProvider } from "@/contexts/BookingContext";
import BookingPageContent from "./BookingPageContent";
import { Service } from "@/types/booking";
import {
  sortAndCapPopularServices,
  isProductListedOnBookingSite,
  isProductStorefrontEnabled,
  shouldLoadStorefrontProducts,
} from "@/lib/utils";
import { isValidTimeZone } from "@/lib/salon-time";
import { useSearchParams } from "react-router-dom";

interface BookingPageWrapperProps {
  businessId: string;
  branchId?: string;
}

export default function BookingPageWrapper({
  businessId,
  branchId,
}: BookingPageWrapperProps) {
  // Fetch business data
  const {
    data: businessData,
    loading: businessLoading,
    error: businessError,
  } = useGetBusinessViaIdentifier(businessId);

  // Extract business and salons
  const business = businessData?.business || null;
  const salons = business?.salons || [];

  const routeSalon = branchId
    ? salons.find(
        (salon: any) =>
          salon.urlIdentifier === branchId || salon.id === branchId,
      )
    : null;
  const selectedSalonId = branchId
    ? routeSalon?.id || ""
    : salons[0]?.id || "";
  const [searchParams] = useSearchParams();
  const hasProductDeepLink = Boolean(searchParams.get("product"));
  const storefrontEnabled = isProductStorefrontEnabled(business);
  const listProductsOnBookingSite = shouldLoadStorefrontProducts(
    business,
    hasProductDeepLink,
  );

  // Fetch salon-specific data (services are fetched here)
  const { data: salonData, loading: salonLoading } =
    useGetSalonForBooking(selectedSalonId);
  const { data: bundlesData } = useGetBundlesForBooking(selectedSalonId);
  const { data: faqsData } = useGetFaqs(selectedSalonId);
  const { data: productsData, loading: productsLoading } =
    useGetStorefrontProducts(selectedSalonId, !listProductsOnBookingSite);
  const { data: reviewsData } = useGetPublicSalonReviews(selectedSalonId);
  const { data: promosData } = useGetPublicSalonPromos(selectedSalonId);

  // Extract data
  const queriedSalonDetails = salonData?.salonForBooking || null;
  const salonDetails =
    queriedSalonDetails?.id === selectedSalonId ? queriedSalonDetails : null;
  const services = (salonDetails?.services || []).filter(
    (service: any) => service.isPublic === true
  );
  const popularServices = sortAndCapPopularServices<Service>(
    (salonDetails?.popularServices || []).filter(
      (service: Service) => service.isPublic === true,
    ),
  );
  const rawBundles = salonDetails
    ? (bundlesData?.bundles || salonDetails.bundles || []).filter(
        (bundle: any) => bundle?.active === true,
      )
    : [];
  const bundles = rawBundles;

  // Extract vouchers - collect from ALL salons across the business, not just the selected one
  const allBusinessPackages = salons.flatMap((s: any) => s.packages || []);
  const packages = allBusinessPackages.filter(
    (pkg: any, index: number, self: any[]) =>
      pkg.isPurchasableAsVoucher === true &&
      pkg.status === "active" &&
      pkg.allowSaleOn === "online" &&
      self.findIndex((p: any) => p.id === pkg.id) === index
  );

  const products = listProductsOnBookingSite
    ? ((productsData?.storefrontProducts || []) as any[]).filter((product) =>
        isProductListedOnBookingSite(product)
      )
    : [];

  const faqs = salonDetails ? faqsData?.faqs || [] : [];
  const reviews = salonDetails ? reviewsData || [] : [];

  // Transform promos from API format to booking format
  const promos = (salonDetails ? promosData || [] : []).map((promo: any) => ({
    id: promo.id,
    title: promo.title || "",
    code: promo.code || promo.title, // Using title as code since API doesn't return code
    description: promo.description,
    discountType: promo.discountType as "percentage" | "fixed",
    discountValue: promo.discountValue,
    validFrom: promo.startAt,
    validTo: promo.endAt,
  }));

  // Merge salon data - enrich the selected salon with detailed data
  const enrichedSalons = salons.map((salon: any) => {
    if (salon.id === selectedSalonId && salonDetails) {
      // Merge all salon details from the API, keeping packages from original
      const enriched = {
        ...salon,
        ...salonDetails,
        // Explicitly keep packages from the original salon query
        packages: salon.packages || [],
        // Explicitly include services with staff pricing data
        services: salonDetails.services || [],
        // Include staff data
        salonStaffs: salonDetails.salonStaffs || [],
        // Include salon hours
        salonHours: salonDetails.salonHours || [],
      };
      return enriched;
    }
    return salon;
  });

  // Only block on business loading - services will load independently
  // This allows the page header and structure to display while services are still loading
  if (businessLoading || (Boolean(selectedSalonId) && productsLoading)) {
    return (
      <div className="min-h-screen bg-grey-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-green-300 mx-auto"></div>
          <p className="mt-4 text-grey-600">Loading booking information...</p>
        </div>
      </div>
    );
  }

  // Error state
  if (businessError) {
    return (
      <div className="min-h-screen bg-grey-50 flex items-center justify-center">
        <div className="text-center">
          <p className="text-red-600 mb-2">Error loading booking information</p>
          <p className="text-grey-600 text-sm">{businessError.message}</p>
        </div>
      </div>
    );
  }

  // No business found
  if (!business) {
    return (
      <div className="min-h-screen bg-grey-50 flex items-center justify-center">
        <div className="text-center">
          <p className="text-grey-900 text-lg mb-2">Business not found</p>
          <p className="text-grey-600">
            The business you are looking for does not exist or has been removed.
          </p>
        </div>
      </div>
    );
  }

  if (branchId && !routeSalon) {
    return (
      <div className="min-h-screen bg-grey-50 flex items-center justify-center">
        <div className="text-center">
          <p className="text-grey-900 text-lg mb-2">Location not found</p>
          <p className="text-grey-600">
            This booking location does not exist or is no longer available.
          </p>
        </div>
      </div>
    );
  }

  const selectedSalon = salons.find(
    (salon: any) => salon.id === selectedSalonId,
  );
  const selectedTimezone = salonDetails?.timezone ?? selectedSalon?.timezone;
  if (!selectedSalon || !isValidTimeZone(selectedTimezone)) {
    return (
      <div className="min-h-screen bg-grey-50 flex items-center justify-center">
        <div className="text-center">
          <p className="text-grey-900 text-lg mb-2">Scheduling unavailable</p>
          <p className="text-grey-600">
            This location does not have a valid scheduling timezone. Please
            contact the business for assistance.
          </p>
        </div>
      </div>
    );
  }

  // Process businessSpecialties - ensure it's an array of strings and format them
  const processedBusiness = {
    ...business,
    businessSpecialties:
      business.businessSpecialties?.map((specialty: any) => {
        // If specialty is already a string, use it
        if (typeof specialty === "string") return specialty;
        
        // Prefer description if available, otherwise transform name
        if (specialty.description) return specialty.description;
        
        // Transform name: replace underscores with spaces and capitalize
        const name = specialty.name;
        return name ? name.replace(/_/g, ' ').replace(/\b\w/g, (l: string) => l.toUpperCase()) : '';
      }).filter(Boolean) || [],
    productStorefrontEnabled: storefrontEnabled,
    productStorefrontAvailable: Boolean(business?.productStorefrontAvailable),
    listProductsOnBookingSite,
  };

  return (
    <BookingProvider
      key={selectedSalonId}
      businessInfo={processedBusiness}
      salons={enrichedSalons}
      services={services}
      popularServices={popularServices}
      packages={packages}
      products={products}
      bundles={bundles}
      faqs={faqs}
      reviews={reviews}
      promos={promos}
      initialSelectedSalonId={selectedSalonId}
      servicesLoading={salonLoading}
    >
      <BookingPageContent />
    </BookingProvider>
  );
}
